
import React, { useState } from 'react'
import {Users, Plus, Search, Filter, Edit, Trash2, Shield, UserCheck, UserX, Clock, Mail, Phone, Building, Calendar, MoreVertical, Eye, UserPlus, Settings, X} from 'lucide-react'
import { useUsuarios } from '../hooks/useUsuarios'
import UsuarioForm from '../components/UsuarioForm'

const Usuarios: React.FC = () => {
  const {
    usuarios,
    loading,
    metrics,
    createUsuario,
    updateUsuario,
    deleteUsuario,
    updateUltimoLogin,
    getPermissoesPorPerfil,
    getPermissoesDetalhadasPorPerfil,
    getModulosDisponiveis
  } = useUsuarios()

  const [showForm, setShowForm] = useState(false)
  const [editingUsuario, setEditingUsuario] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPerfil, setFilterPerfil] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [selectedUsuarios, setSelectedUsuarios] = useState<string[]>([])
  const [showPermissionsModal, setShowPermissionsModal] = useState<any>(null)

  // Filtrar usuários
  const filteredUsuarios = usuarios.filter(usuario => {
    const matchesSearch = 
      usuario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (usuario.departamento && usuario.departamento.toLowerCase().includes(searchTerm.toLowerCase()))
    
    const matchesPerfil = !filterPerfil || usuario.perfil === filterPerfil
    const matchesStatus = !filterStatus || usuario.status === filterStatus
    
    return matchesSearch && matchesPerfil && matchesStatus
  })

  const handleCreateUsuario = async (usuarioData: any) => {
    try {
      await createUsuario(usuarioData)
      setShowForm(false)
    } catch (error) {
      console.error('Erro ao criar usuário:', error)
    }
  }

  const handleUpdateUsuario = async (usuarioData: any) => {
    if (!editingUsuario) return
    
    try {
      await updateUsuario(editingUsuario._id, usuarioData)
      setEditingUsuario(null)
      setShowForm(false)
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error)
    }
  }

  const handleDeleteUsuario = async (id: string, nome: string) => {
    if (window.confirm(`Tem certeza que deseja excluir o usuário "${nome}"?`)) {
      try {
        await deleteUsuario(id)
      } catch (error) {
        console.error('Erro ao excluir usuário:', error)
      }
    }
  }

  const handleEditUsuario = (usuario: any) => {
    setEditingUsuario(usuario)
    setShowForm(true)
  }

  const handleSelectUsuario = (id: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedUsuarios(prev => [...prev, id])
    } else {
      setSelectedUsuarios(prev => prev.filter(uid => uid !== id))
    }
  }

  const getPerfilColor = (perfil: string) => {
    const colors: Record<string, string> = {
      administrador: 'bg-red-100 text-red-800',
      juridico: 'bg-purple-100 text-purple-800',
      financeiro: 'bg-green-100 text-green-800',
      compras: 'bg-blue-100 text-blue-800',
      engenheiro: 'bg-orange-100 text-orange-800',
      gestor: 'bg-indigo-100 text-indigo-800'
    }
    return colors[perfil] || 'bg-gray-100 text-gray-800'
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      ativo: 'bg-green-100 text-green-800',
      inativo: 'bg-gray-100 text-gray-800',
      bloqueado: 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const perfilLabels: Record<string, string> = {
    administrador: 'Administrador',
    juridico: 'Jurídico',
    financeiro: 'Financeiro',
    compras: 'Compras',
    engenheiro: 'Engenheiro',
    gestor: 'Gestor'
  }

  const getPermissionsCount = (usuario: any) => {
    if (!usuario.permissoesDetalhadas) return 0
    
    let count = 0
    Object.values(usuario.permissoesDetalhadas).forEach((modulo: any) => {
      if (modulo.visualizar) count++
      if (modulo.adicionar) count++
      if (modulo.editar) count++
      if (modulo.excluir) count++
    })
    
    return count
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestão de Usuários</h1>
          <p className="text-gray-600 mt-1">Gerencie usuários e permissões granulares do sistema</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <UserPlus className="w-5 h-5 mr-2" />
          Novo Usuário
        </button>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Usuários</p>
              <p className="text-2xl font-bold text-gray-900">{metrics.totalUsuarios}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Usuários Ativos</p>
              <p className="text-2xl font-bold text-green-600">{metrics.usuariosAtivos}</p>
            </div>
            <UserCheck className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Usuários Inativos</p>
              <p className="text-2xl font-bold text-gray-600">{metrics.usuariosInativos}</p>
            </div>
            <UserX className="w-8 h-8 text-gray-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Logins Recentes</p>
              <p className="text-2xl font-bold text-blue-600">{metrics.loginsRecentes}</p>
              <p className="text-xs text-gray-500">Últimos 7 dias</p>
            </div>
            <Clock className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Distribuição por Perfil */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuição por Perfil</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(metrics.distribuicaoPorPerfil).map(([perfil, count]) => (
            <div key={perfil} className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{count}</p>
              <p className="text-sm text-gray-600">{perfilLabels[perfil]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar usuários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <select
            value={filterPerfil}
            onChange={(e) => setFilterPerfil(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Todos os Perfis</option>
            {Object.entries(perfilLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Todos os Status</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
            <option value="bloqueado">Bloqueado</option>
          </select>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {filteredUsuarios.length} usuário(s) encontrado(s)
            </span>
          </div>
        </div>
      </div>

      {/* Lista de Usuários */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsuarios(filteredUsuarios.map(u => u._id))
                      } else {
                        setSelectedUsuarios([])
                      }
                    }}
                    checked={selectedUsuarios.length === filteredUsuarios.length && filteredUsuarios.length > 0}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Perfil
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Departamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissões
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsuarios.map((usuario) => (
                <tr key={usuario._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsuarios.includes(usuario._id)}
                      onChange={(e) => handleSelectUsuario(usuario._id, e.target.checked)}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-blue-600">
                            {usuario.nome.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{usuario.nome}</div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {usuario.email}
                        </div>
                        {usuario.telefone && (
                          <div className="text-sm text-gray-500 flex items-center">
                            <Phone className="w-4 h-4 mr-1" />
                            {usuario.telefone}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPerfilColor(usuario.perfil)}`}>
                      {perfilLabels[usuario.perfil]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(usuario.status)}`}>
                      {usuario.status.charAt(0).toUpperCase() + usuario.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{usuario.departamento || '-'}</div>
                    <div className="text-sm text-gray-500">{usuario.cargo || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">
                        {getPermissionsCount(usuario)} permissões
                      </span>
                      <button
                        onClick={() => setShowPermissionsModal(usuario)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="Ver permissões detalhadas"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {usuario.ultimoLogin ? (
                      <div>
                        <div>{new Date(usuario.ultimoLogin).toLocaleDateString('pt-BR')}</div>
                        <div className="text-xs">{new Date(usuario.ultimoLogin).toLocaleTimeString('pt-BR')}</div>
                      </div>
                    ) : (
                      'Nunca'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditUsuario(usuario)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="Editar usuário"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteUsuario(usuario._id, usuario.nome)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Excluir usuário"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsuarios.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum usuário encontrado</p>
            <p className="text-sm text-gray-400">Tente ajustar os filtros ou criar um novo usuário</p>
          </div>
        )}
      </div>

      {/* Modal de Permissões Detalhadas */}
      {showPermissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Permissões de {showPermissionsModal.nome}
              </h2>
              <button
                onClick={() => setShowPermissionsModal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {getModulosDisponiveis().map((modulo) => {
                  const permissoes = showPermissionsModal.permissoesDetalhadas?.[modulo.key] || {}
                  return (
                    <div key={modulo.key} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-3">{modulo.label}</h3>
                      <div className="space-y-2">
                        {['visualizar', 'adicionar', 'editar', 'excluir'].map((acao) => (
                          <div key={acao} className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${permissoes[acao] ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <span className={`text-sm ${permissoes[acao] ? 'text-gray-900' : 'text-gray-500'}`}>
                              {acao.charAt(0).toUpperCase() + acao.slice(1)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formulário de Usuário */}
      {showForm && (
        <UsuarioForm
          usuario={editingUsuario}
          onSubmit={editingUsuario ? handleUpdateUsuario : handleCreateUsuario}
          onCancel={() => {
            setShowForm(false)
            setEditingUsuario(null)
          }}
          getPermissoesPorPerfil={getPermissoesPorPerfil}
          getPermissoesDetalhadasPorPerfil={getPermissoesDetalhadasPorPerfil}
          getModulosDisponiveis={getModulosDisponiveis}
        />
      )}
    </div>
  )
}

export default Usuarios
